import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Factory, Cpu, Satellite, Truck, Recycle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

const Vision2031Section = () => {
  const { t, i18n } = useTranslation();

  const UAE = { x: 508, y: 173 };
  const [time, setTime] = useState(0);
  const [vw, setVw] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    let raf;
    let last = performance.now();
    const loop = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      setTime((t) => t + dt);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const isMobile = vw < 768;
  const scale = isMobile ? 0.7 : 1;

  // Cache translation labels to avoid calling t() every animation frame
  const sectorLabels = useMemo(() => ({
    manufacturing: t("EmiratesMade.Vision2031.sector.Manufacturing", { defaultValue: "Manufacturing" }),
    technology: t("EmiratesMade.Vision2031.sector.Technology", { defaultValue: "Technology" }),
    aerospace: t("EmiratesMade.Vision2031.sector.Aerospace", { defaultValue: "Aerospace" }),
    logistics: t("EmiratesMade.Vision2031.sector.Logistics", { defaultValue: "Logistics" }),
    sustainability: t("EmiratesMade.Vision2031.sector.Sustainability", { defaultValue: "Sustainability" }),
  }), [i18n.language, t]);

  const pinConfig = useMemo(() => ([
    {
      C: Factory,
      base: { x: 430, y: 130 },
      amp: { x: 24, y: 14 },
      speed: 0.6,
      phase: 0.0,
      href: "#manufacturing",
      label: sectorLabels.manufacturing,
    },
    {
      C: Cpu,
      base: { x: 720, y: 120 },
      amp: { x: 18, y: 16 },
      speed: 0.55,
      phase: 1.3,
      href: "#innovation",
      label: sectorLabels.technology,
    },
    {
      C: Satellite,
      base: { x: 120, y: 160 },
      amp: { x: 22, y: 18 },
      speed: 0.5,
      phase: 2.2,
      href: "#aerospace",
      label: sectorLabels.aerospace,
    },
    {
      C: Truck,
      base: { x: 300, y: 260 },
      amp: { x: 20, y: 12 },
      speed: 0.58,
      phase: 0.8,
      href: "#logistics",
      label: sectorLabels.logistics,
    },
    {
      C: Recycle,
      base: { x: 700, y: 300 },
      amp: { x: 16, y: 14 },
      speed: 0.52,
      phase: 1.9,
      href: "#sustainability",
      label: sectorLabels.sustainability,
    },
  ]), [sectorLabels]);

  const pins = pinConfig.map((p, i) => {
    const a = time * p.speed + p.phase;
    const x = p.base.x + (p.amp.x * scale) * Math.sin(a);
    const y = p.base.y + (p.amp.y * scale) * Math.cos(a * 1.15);
    return { ...p, x, y, key: `pin-${i}` };
  });

  return (
    <section
      id="vision2031"
      className="relative px-4 md:px-6 py-16 md:py-24 overflow-hidden text-slate-800"
      style={{ background: "linear-gradient(180deg, #f7fbf9 0%, #ffffff 85%)" }}
    >
      {/* Gradient orbs */}
      <motion.div
        className="absolute w-[420px] h-[420px] rounded-full blur-3xl -left-24 -top-10"
        style={{ background: "color-mix(in oklab, var(--uae-green) 14%, transparent)" }}
        animate={{ x: [0, 80, -60, 0], y: [0, -60, 40, 0] }}
        transition={{ duration: 12, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" }}
      />
      <motion.div
        className="absolute w-[260px] h-[260px] rounded-full blur-3xl right-0 top-20"
        style={{ background: "color-mix(in oklab, var(--uae-red) 12%, transparent)" }}
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 10, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" }}
      />

      <div className="relative z-10 max-w-7xl mx-auto grid md:grid-cols-2 gap-10 items-center">
        {/* Left side: animated map */}
        <div className="relative vision-map" style={{ height: isMobile ? 280 : 420 }}>
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/8/80/World_map_-_low_resolution.svg"
            alt="World map"
            className="absolute inset-0 w-full h-full object-contain pointer-events-none"
            style={{ opacity: isMobile ? 0.45 : 0.35, filter: "grayscale(100%) contrast(110%)" }}
          />
          <svg viewBox="0 0 800 420" className="absolute inset-0 w-full h-full">
            <radialGradient id="glow" cx="560" cy="210" r="200" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="rgba(90,62,200,0.25)" />
              <stop offset="100%" stopColor="rgba(90,62,200,0)" />
            </radialGradient>
            <circle cx={UAE.x} cy={UAE.y} r="180" fill="url(#glow)" />
            <circle cx={UAE.x} cy={UAE.y} r="6" fill="var(--uae-red)" />
            <text x={UAE.x + 8} y={UAE.y - 4} fontSize="10" fill="#0a2750">
              UAE
            </text>
            {pins.map((d, i) => {
              const sx = UAE.x,
                sy = UAE.y;
              const cx = (sx + d.x) / 2 + (i % 2 === 0 ? 60 * scale : -60 * scale);
              const cy = Math.min(sy, d.y) - 80 * scale - i * 6;
              const path = `M ${sx} ${sy} Q ${cx} ${cy} ${d.x} ${d.y}`;
              return (
                <motion.path
                  key={`arc-${i}`}
                  d={path}
                  fill="none"
                  stroke="var(--uae-green)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray="6 8"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1, strokeDashoffset: [0, -60] }}
                  transition={{ duration: 5 + i, repeat: Infinity, ease: "easeInOut" }}
                />
              );
            })}
          </svg>
          {pins.map(({ C, x, y, label }, i) => (
            <div
              key={`float-pin-${i}`}
              className="absolute flex items-center justify-center w-10 h-10 -mt-5 -ml-5 rounded-full bg-white shadow-md"
              style={{
                top: `${(y / 420) * 100}%`,
                left: `${(x / 800) * 100}%`,
                border: "1px solid rgba(90,62,200,0.18)",
              }}
              title={label}
            >
              <C size={18} style={{ color: "var(--uae-green)" }} />
            </div>
          ))}
        </div>

        {/* Right side: text */}
        <div className="text-left md:pl-8">
          <motion.h1
            className="text-5xl md:text-6xl font-extrabold drop-shadow-lg"
            style={{
              backgroundImage:
                "linear-gradient(90deg, var(--uae-green), var(--uae-black), var(--uae-red))",
              WebkitBackgroundClip: "text",
              color: "transparent",
            }}
          >
            {t("EmiratesMade.Vision2031.title", { defaultValue: "Vision 2031" })}
          </motion.h1>

          <motion.p
            className="mt-6 text-lg md:text-xl max-w-3xl leading-relaxed text-slate-700"
          >
            <span style={{ color: "var(--uae-black)", fontWeight: 500 }}>
              {t("EmiratesMade.Vision2031.p1", { defaultValue: "Aligned with the UAE Vision 2031, Emirates Made supports the nation’s industrial transformation, innovation ecosystem, and diversification goals." })}
            </span>
          </motion.p>

          <motion.div className="mt-8 bg-white/70 backdrop-blur-sm rounded-2xl p-6 shadow-lg max-w-3xl">
            <p className="text-lg md:text-xl text-slate-700 font-medium">
              {t("EmiratesMade.Vision2031.p2", { defaultValue: "The initiative aims to position the UAE as a global center for advanced industries, creative entrepreneurship, and technology-driven exports." })
                .split(/(global center|advanced industries|creative entrepreneurship|technology-driven exports)/)
                .map((part, index) =>
                  ["global center", "advanced industries"].includes(part) ? (
                    <span key={index} style={{ color: "var(--uae-green)", fontWeight: 700 }}>
                      {part}
                    </span>
                  ) : part === "creative entrepreneurship" ? (
                    <span key={index} style={{ color: "var(--uae-black)", fontWeight: 700 }}>
                      {part}
                    </span>
                  ) : part === "technology-driven exports" ? (
                    <span key={index} style={{ color: "var(--uae-red)", fontWeight: 700 }}>
                      {part}
                    </span>
                  ) : (
                    part
                  )
                )}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Vision2031Section;
